import { configureStore } from '@reduxjs/toolkit';
import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { combineReducers } from 'redux';

// Import reducers
import authReducer from './slices/authSlice';
import partnerReducer from './slices/partnerSlice';
import mothersReducer from './slices/mothersSlice';
import subscriptionReducer from './slices/subscriptionSlice';
import matchFundingReducer from './slices/matchFundingSlice';
import contributionsReducer from './slices/contributionsSlice';
import activitiesReducer from './slices/activitiesSlice';
import uiReducer from './slices/uiSlice';

// Persist configuration
const persistConfig = {
  key: 'root',
  storage,
  whitelist: ['auth', 'partner', 'ui'], // Only persist these reducers
  blacklist: ['mothers', 'subscription', 'matchFunding', 'contributions', 'activities'], // Don't persist these
  // Add performance optimizations
  serialize: true,
  deserialize: true,
  timeout: 10000, // 10 second timeout
};

// Root reducer
const rootReducer = combineReducers({
  auth: authReducer,
  partner: partnerReducer,
  mothers: mothersReducer,
  subscription: subscriptionReducer,
  matchFunding: matchFundingReducer,
  contributions: contributionsReducer,
  activities: activitiesReducer,
  ui: uiReducer,
});

// Persisted reducer
const persistedReducer = persistReducer(persistConfig, rootReducer);

// Store configuration with performance optimizations
export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [
          'persist/PERSIST', 
          'persist/REHYDRATE',
          'persist/PAUSE',
          'persist/PURGE',
          'persist/REGISTER',
          'persist/FLUSH'
        ],
        ignoredPaths: ['mothers.mothers', 'mothers.partnerInfo'],
        warnAfter: 128,
      },
      immutableCheck: {
        warnAfter: 128,
      },
    }),
  devTools: process.env.NODE_ENV !== 'production',
  // Performance optimizations
  preloadedState: undefined,
});

// Persistor with performance optimizations
export const persistor = persistStore(store, {
  manualPersist: false,
  // Add performance callbacks
  beforeLift: () => {
    console.log('Redux Persist: Starting to lift state...');
  },
  afterLift: () => {
    console.log('Redux Persist: State lifted successfully');
  },
});

// Root state and dispatch types
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
